import { BOARD_HEIGHT, BOARD_WIDTH } from '../engine/constants';
import { ACTIONS, ACTION_LABELS, type Action } from './input';

export interface Layout {
  root: HTMLElement;
  boardCells: HTMLElement[][]; // [row][col], row 0 = bottom (matches engine convention)
  timerEl: HTMLElement;
  holdGrid: HTMLElement;
  nextGrid: HTMLElement;
  nextSlots: HTMLElement[];
  statPieces: HTMLElement;
  statPiecesSub: HTMLElement;
  statFinesse: HTMLElement;
  statFinesseSub: HTMLElement;
  statLevel: HTMLElement;
  statScore: HTMLElement;
  puzzleFinesse: HTMLElement;
  puzzleFinesseSub: HTMLElement;
  puzzleLabel: HTMLElement;
  pieceCountInput: HTMLInputElement;
  newPuzzleBtn: HTMLButtonElement;
  settingsBtn: HTMLButtonElement;
  settingsBackdrop: HTMLElement;
  settingsClose: HTMLButtonElement;
  keybindInputs: Record<Action, HTMLInputElement>;
  dasInput: HTMLInputElement;
  arrInput: HTMLInputElement;
  toast: HTMLElement;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (className) e.className = className;
  if (text !== undefined) e.textContent = text;
  return e;
}

export function buildLayout(root: HTMLElement): Layout {
  root.innerHTML = '';
  const app = el('div', 'app');

  const layoutGrid = el('div', 'layout');

  // ---- left column: HOLD + session stats ----
  const colLeft = el('div', 'col col-left');
  const holdPanel = el('div', 'panel hold-panel');
  holdPanel.append(el('div', 'panel-tab', 'HOLD'));
  const holdGrid = el('div', 'preview-grid');
  const holdGridWrap = el('div', 'preview-grid-wrap');
  holdGridWrap.append(holdGrid);
  holdPanel.append(holdGridWrap);
  colLeft.append(holdPanel);

  const statsLeft = el('div', 'panel stats-panel');
  const statPieces = statRow(statsLeft, 'PIECES');
  const statFinesse = statRow(statsLeft, 'FINESSE');
  const statLevel = statRow(statsLeft, 'LEVEL');
  const statScore = statRow(statsLeft, 'SCORE');
  colLeft.append(statsLeft);

  // ---- center column: board + toolbar ----
  const colCenter = el('div', 'col col-center');
  const boardWrap = el('div', 'board-wrap');
  const boardEl = el('div', 'board');
  const boardCells: HTMLElement[][] = Array.from({ length: BOARD_HEIGHT }, () => []);
  for (let displayRow = 0; displayRow < BOARD_HEIGHT; displayRow++) {
    const gameRow = BOARD_HEIGHT - 1 - displayRow;
    for (let col = 0; col < BOARD_WIDTH; col++) {
      const cell = el('div', 'cell');
      boardEl.append(cell);
      boardCells[gameRow][col] = cell;
    }
  }
  const timerEl = el('div', 'timer-overlay', '0:00');
  boardWrap.append(boardEl, timerEl);
  colCenter.append(boardWrap);

  const toolbar = el('div', 'toolbar');
  const pieceCountLabel = el('label', 'piece-count-label', 'Pieces');
  const pieceCountInput = el('input', 'piece-count-input');
  pieceCountInput.type = 'number';
  pieceCountInput.min = '2';
  pieceCountInput.max = '7';
  pieceCountInput.value = '7';
  pieceCountLabel.append(pieceCountInput);
  const newPuzzleBtn = el('button', 'btn btn-accent', 'New Puzzle');
  const settingsBtn = el('button', 'btn btn-ghost', '⚙ Controls');
  toolbar.append(pieceCountLabel, newPuzzleBtn, settingsBtn);
  colCenter.append(toolbar);

  // ---- right column: NEXT + puzzle stats ----
  const colRight = el('div', 'col col-right');
  const nextPanel = el('div', 'panel next-panel');
  nextPanel.append(el('div', 'panel-tab', 'NEXT'));
  const nextGrid = el('div', 'next-grid');
  const nextSlots: HTMLElement[] = [];
  for (let i = 0; i < 5; i++) {
    const slotWrap = el('div', 'preview-grid-wrap next-slot');
    const slot = el('div', 'preview-grid');
    slotWrap.append(slot);
    nextGrid.append(slotWrap);
    nextSlots.push(slot);
  }
  nextPanel.append(nextGrid);
  colRight.append(nextPanel);

  const statsRight = el('div', 'panel stats-panel');
  const puzzleLabel = el('div', 'puzzle-label', 'THIS PUZZLE');
  statsRight.append(puzzleLabel);
  const puzzleFinesse = statRow(statsRight, 'FINESSE');
  colRight.append(statsRight);

  layoutGrid.append(colLeft, colCenter, colRight);
  app.append(layoutGrid);

  // ---- settings modal ----
  const settingsBackdrop = el('div', 'modal-backdrop');
  settingsBackdrop.hidden = true;
  const modal = el('div', 'modal');
  modal.append(el('h2', 'modal-title', 'Controls'));

  const table = el('table', 'keybind-table');
  const keybindInputs = {} as Record<Action, HTMLInputElement>;
  for (const action of ACTIONS) {
    const row = el('tr');
    row.append(el('td', 'keybind-label', ACTION_LABELS[action]));
    const cell = el('td');
    const input = el('input', 'keybind-input');
    input.type = 'text';
    input.readOnly = true;
    input.dataset.action = action;
    cell.append(input);
    row.append(cell);
    table.append(row);
    keybindInputs[action] = input;
  }
  modal.append(table);

  const dasArrRow = el('div', 'das-arr-row');
  const dasLabel = el('label', 'das-arr-label', 'DAS (ms)');
  const dasInput = el('input', 'das-arr-input');
  dasInput.type = 'number';
  dasInput.min = '1';
  dasInput.max = '200';
  dasLabel.append(dasInput);
  const arrLabel = el('label', 'das-arr-label', 'ARR (ms)');
  const arrInput = el('input', 'das-arr-input');
  arrInput.type = 'number';
  arrInput.min = '0';
  arrInput.max = '100';
  arrLabel.append(arrInput);
  dasArrRow.append(dasLabel, arrLabel);
  modal.append(dasArrRow);

  const hint = el('p', 'modal-hint', 'Click a binding, then press any key to rebind.');
  modal.append(hint);

  const settingsClose = el('button', 'btn btn-accent', 'Done');
  modal.append(settingsClose);
  settingsBackdrop.append(modal);
  app.append(settingsBackdrop);

  const toast = el('div', 'toast');
  toast.hidden = true;
  app.append(toast);

  root.append(app);

  return {
    root: app,
    boardCells,
    timerEl,
    holdGrid,
    nextGrid,
    nextSlots,
    statPieces: statPieces.value,
    statPiecesSub: statPieces.sub,
    statFinesse: statFinesse.value,
    statFinesseSub: statFinesse.sub,
    statLevel: statLevel.value,
    statScore: statScore.value,
    puzzleFinesse: puzzleFinesse.value,
    puzzleFinesseSub: puzzleFinesse.sub,
    puzzleLabel,
    pieceCountInput,
    newPuzzleBtn,
    settingsBtn,
    settingsBackdrop,
    settingsClose,
    keybindInputs,
    dasInput,
    arrInput,
    toast,
  };
}

function statRow(parent: HTMLElement, label: string): { value: HTMLElement; sub: HTMLElement } {
  const row = el('div', 'stat-row');
  row.append(el('div', 'stat-label', label));
  const value = el('div', 'stat-value', '—');
  const sub = el('div', 'stat-sub');
  row.append(value, sub);
  parent.append(row);
  return { value, sub };
}
